/**
 * MergeContactsDialog — CIE-3 Multi-Contact Merge Wizard
 *
 * Supports merging 2 or more contacts with:
 *  - Multi-contact selection (initial + add more)
 *  - Survivor recommendation (scoring-based, staff override)
 *  - N-way conflict resolution
 *  - Tax Profile comparison
 *  - Dependency counts + portal warnings
 *  - Final confirmation + post-merge reconciliation
 */
import { useState, useEffect, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const STEP_LABELS = ["Select contacts", "Survivor", "Resolve & review", "Confirm"];

// ── Shared sub-components ───────────────────────────────────────────────────

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-1 mb-5" data-testid="merge-step-indicator">
      {STEP_LABELS.map((label, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0
            ${i < step ? "bg-blue-600 text-white" : i === step
              ? "bg-blue-100 text-blue-700 border-2 border-blue-600" : "bg-slate-100 text-slate-400"}`}>
            {i < step ? <Icons.Check className="h-3 w-3" /> : i + 1}
          </div>
          <span className={`text-xs hidden sm:inline ${i === step ? "text-blue-700 font-medium" : "text-slate-400"}`}>
            {label}
          </span>
          {i < STEP_LABELS.length - 1 && (
            <div className={`w-4 h-px ${i < step ? "bg-blue-400" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function LifecyclePill({ status }) {
  const cfg = {
    active_client: "bg-emerald-50 text-emerald-700 border-emerald-200",
    inactive_client: "bg-slate-100 text-slate-600 border-slate-200",
    lead: "bg-blue-50 text-blue-700 border-blue-200",
    prospect: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const labels = {
    active_client: "Active Client", inactive_client: "Inactive",
    lead: "Lead", prospect: "Lead",
  };
  return (
    <span className={`text-[10px] border rounded-full px-2 py-0.5 font-medium
      ${cfg[status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
      {labels[status] || status}
    </span>
  );
}

function ContactCard({ contact, label, selected, onSelect, radioName, onRemove, showRemove }) {
  const inner = (
    <div className={`flex items-start gap-3 p-3 rounded-xl border transition-all
      ${selected ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
      {onSelect && (
        <RadioGroupItem value={contact.id} id={`${radioName}-${contact.id}`} className="mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {label && (
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded
              ${label === "Survivor" || label === "Recommended" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
              {label}
            </span>
          )}
          <span className="text-sm font-semibold text-slate-800 truncate">{contact.full_name || "—"}</span>
          <span className="text-xs text-slate-400">{contact.client_number}</span>
        </div>
        <div className="mt-1 space-y-0.5">
          {contact.email && <div className="text-xs text-slate-500 truncate">{contact.email}</div>}
          {(contact.mobile_phone || contact.phone) && (
            <div className="text-xs text-slate-500">{contact.mobile_phone || contact.phone}</div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <LifecyclePill status={contact.lifecycle_status} />
            {contact.portal_status && (
              <span className="text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5">
                Portal: {contact.portal_status}
              </span>
            )}
          </div>
        </div>
      </div>
      {showRemove && onRemove && (
        <button onClick={(e) => { e.preventDefault(); onRemove(contact.id); }}
          data-testid={`merge-remove-${contact.id}`}
          className="text-slate-300 hover:text-red-400 shrink-0 mt-0.5 transition-colors">
          <Icons.X className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  if (onSelect) {
    return (
      <label htmlFor={`${radioName}-${contact.id}`} className="cursor-pointer block">
        {inner}
      </label>
    );
  }
  return inner;
}

function ScoreBar({ score, maxScore = 120 }) {
  const pct = Math.min(100, Math.round((score / maxScore) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500 w-7 text-right">{score}</span>
    </div>
  );
}

function ConflictRow({ conflict, value, onChange }) {
  const options = conflict.sources
    ? [{ label: "Survivor", val: conflict.survivor_value }, ...conflict.sources.map((s) => ({
        label: `Duplicate`, val: s.value, dupId: s.duplicate_id,
      }))]
    : [
        { label: "Survivor", val: conflict.survivor_value },
        { label: "Duplicate", val: conflict.duplicate_value },
      ];

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
          {conflict.field}
        </span>
        <span className="text-[10px] text-amber-600">Staff choice required</span>
      </div>
      <div className={`grid gap-2 mb-2 ${options.length <= 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3"}`}>
        {options.map((opt, i) => opt.val !== null && opt.val !== undefined ? (
          <button key={i} onClick={() => onChange(conflict.field, opt.val)}
            className={`text-xs px-2 py-1.5 rounded-lg border transition-all text-left truncate
              ${value === opt.val ? "bg-blue-600 text-white border-blue-600"
                : "bg-white border-slate-300 hover:border-blue-400 text-slate-700"}`}>
            <div className="text-[9px] opacity-70 mb-0.5">{opt.label}</div>
            {String(opt.val)}
          </button>
        ) : null)}
      </div>
    </div>
  );
}

const TP_SKIP = "__SKIP__";

function TpConflictRow({ conflict, value, onChange }) {
  const options = [
    ...(conflict.survivor_value !== null && conflict.survivor_value !== undefined
      ? [{ label: "Survivor (keep)", val: conflict.survivor_value, style: "survivor" }]
      : []),
    ...conflict.sources.map((s) => ({ label: "Use duplicate", val: s.value, style: "duplicate" })),
  ];
  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-purple-800 uppercase tracking-wide">
          {conflict.field}
        </span>
        {conflict.mandatory_review && (
          <span className="text-[10px] text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">Mandatory review</span>
        )}
      </div>
      <div className={`grid gap-2 ${options.length <= 1 ? "grid-cols-2" : options.length === 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3"}`}>
        {options.map((opt, i) => opt.val !== null && opt.val !== undefined ? (
          <button key={i} onClick={() => onChange(conflict.field, opt.val)}
            data-testid={`tp-conflict-option-${conflict.field}-${opt.style}`}
            className={`text-xs px-2 py-1.5 rounded-lg border transition-all text-left truncate
              ${value === opt.val ? "bg-blue-600 text-white border-blue-600"
                : "bg-white border-slate-300 hover:border-blue-400 text-slate-700"}`}>
            <div className="text-[9px] opacity-70 mb-0.5">{opt.label}</div>
            {String(opt.val)}
          </button>
        ) : null)}
        {/* SKIP option — always offered for mandatory review; also available for all TP conflicts */}
        <button
          onClick={() => onChange(conflict.field, TP_SKIP)}
          data-testid={`tp-conflict-skip-${conflict.field}`}
          className={`text-xs px-2 py-1.5 rounded-lg border transition-all text-left
            ${value === TP_SKIP
              ? "bg-amber-500 text-white border-amber-500"
              : "bg-white border-slate-300 hover:border-amber-400 text-slate-600"}`}>
          <div className="text-[9px] opacity-70 mb-0.5">Leave unchanged</div>
          Skip import
        </button>
      </div>
      {value === TP_SKIP && (
        <p className="mt-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          data-testid={`tp-conflict-skip-notice-${conflict.field}`}>
          This field will remain absent on the surviving record — no value will be written.
        </p>
      )}
    </div>
  );
}

// ── Main dialog ──────────────────────────────────────────────────────────────

export function MergeContactsDialog({ open, onOpenChange, initialContact, onMerged }) {
  const [step, setStep] = useState(0);

  // Step 0 — contact selection
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Step 1 — survivor recommendation
  const [recommendation, setRecommendation] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [survivorId, setSurvivorId] = useState(null);

  // Step 2 — preflight + conflict resolution
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [tpResolutions, setTpResolutions] = useState({});

  // Step 3 — execution
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  const reset = useCallback(() => {
    setStep(0); setSearchQ(""); setSearchResults([]); setSearching(false);
    setSelectedContacts([]); setRecommendation(null); setLoadingRec(false);
    setSurvivorId(null); setPreflight(null); setPreflightLoading(false);
    setConflictResolutions({}); setTpResolutions({}); setMerging(false); setMergeResult(null);
  }, []);

  useEffect(() => { if (!open) reset(); }, [open, reset]);

  // Initialise selected contacts from initialContact
  useEffect(() => {
    if (open && initialContact && selectedContacts.length === 0) {
      setSelectedContacts([initialContact]);
    }
  }, [open, initialContact]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contact search
  useEffect(() => {
    if (!searchQ.trim() || searchQ.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/crm/contacts", { params: { q: searchQ, limit: 8 } });
        const selectedIds = new Set(selectedContacts.map((c) => c.id));
        const filtered = (data.contacts || []).filter((c) => !selectedIds.has(c.id));
        setSearchResults(filtered);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQ, selectedContacts]);

  const addContact = (contact) => {
    setSelectedContacts((prev) => [...prev, contact]);
    setSearchQ("");
    setSearchResults([]);
  };

  const removeContact = (contactId) => {
    if (contactId === initialContact?.id) return; // cannot remove initial contact
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const proceedToRecommend = async () => {
    if (selectedContacts.length < 2) return;
    setLoadingRec(true);
    try {
      const { data } = await api.post("/crm/contacts/merge/recommend", {
        contact_ids: selectedContacts.map((c) => c.id),
      });
      setRecommendation(data);
      setSurvivorId(data.recommended_survivor_id);
      setStep(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recommendation failed");
    } finally { setLoadingRec(false); }
  };

  const proceedToReview = async () => {
    if (!survivorId) return;
    const dupIds = selectedContacts.map((c) => c.id).filter((id) => id !== survivorId);
    setPreflightLoading(true);
    try {
      const { data } = await api.post("/crm/contacts/merge/multi/preflight", {
        survivor_id: survivorId,
        duplicate_ids: dupIds,
      });
      if (data.already_merged_ids?.length === dupIds.length) {
        toast.info("All selected contacts are already merged into the survivor.");
        onOpenChange(false);
        return;
      }
      setPreflight(data);
      const conflictDefaults = {};
      (data.unresolved_conflicts || []).forEach((c) => {
        conflictDefaults[c.field] = c.suggested;
      });
      setConflictResolutions(conflictDefaults);
      const tpDefaults = {};
      (data.tp_conflicts || []).forEach((c) => {
        if (c.suggested !== null && c.suggested !== undefined) {
          tpDefaults[c.field] = c.suggested;
        }
      });
      setTpResolutions(tpDefaults);
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preflight failed");
    } finally { setPreflightLoading(false); }
  };

  const resolveConflict = (field, value) => setConflictResolutions((p) => ({ ...p, [field]: value }));
  const resolveTpConflict = (field, value) => setTpResolutions((p) => ({ ...p, [field]: value }));

  // Mandatory TP conflicts must all be resolved
  const mandatoryTpUnresolved = (preflight?.tp_conflicts || []).filter(
    (c) => c.mandatory_review && tpResolutions[c.field] === undefined
  ).length;
  const unresolvedConflicts = (preflight?.unresolved_conflicts || []).filter(
    (c) => conflictResolutions[c.field] === undefined
  ).length;
  const hasBlockers = (preflight?.blockers?.length || 0) > 0;
  const canContinue = !hasBlockers && unresolvedConflicts === 0;

  const executeMerge = async () => {
    if (!preflight || !survivorId) return;
    const dupIds = selectedContacts.map((c) => c.id).filter((id) => id !== survivorId);
    setMerging(true);
    try {
      const { data } = await api.post("/crm/contacts/merge/multi", {
        survivor_id: survivorId,
        duplicate_ids: dupIds,
        field_overrides: Object.keys(conflictResolutions).length ? conflictResolutions : undefined,
        tp_field_overrides: Object.keys(tpResolutions).length ? tpResolutions : undefined,
      });
      setMergeResult(data);
      toast.success(`Merge complete — ${data.retired_contacts?.length || 0} record(s) retired`);
      onMerged?.(data.primary_id);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Merge failed");
    } finally { setMerging(false); }
  };

  if (!initialContact) return null;

  const survivorContact = selectedContacts.find((c) => c.id === survivorId);
  const duplicateContacts = selectedContacts.filter((c) => c.id !== survivorId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Icons.GitMerge className="h-5 w-5 text-blue-600" />
            Merge Contacts
          </DialogTitle>
        </DialogHeader>

        <StepIndicator step={step} />

        {/* ── Step 0: Select contacts ─────────────────────────────────────── */}
        {step === 0 && (
          <div className="space-y-4" data-testid="merge-step-search">
            {/* Selected contacts list */}
            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">
                Selected contacts ({selectedContacts.length}):
              </div>
              <div className="space-y-1.5" data-testid="merge-selected-contacts">
                {selectedContacts.map((c) => (
                  <ContactCard key={c.id} contact={c}
                    showRemove={c.id !== initialContact?.id}
                    onRemove={removeContact}
                    data-testid={`merge-selected-${c.id}`}
                  />
                ))}
              </div>
            </div>

            {/* Search for additional contacts */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">
                Add another contact to merge:
              </label>
              <div className="relative">
                <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="merge-contact-search"
                  placeholder="Name, email, phone, or client number…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  className="pl-9"
                  autoFocus={selectedContacts.length >= 2 ? false : true}
                />
                {searching && (
                  <Icons.Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
              </div>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="merge-search-results">
                {searchResults.map((c) => (
                  <button key={c.id} onClick={() => addContact(c)}
                    data-testid={`merge-select-${c.id}`}
                    className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all group">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-slate-800">{c.full_name || "—"}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {c.email} {c.client_number && `· ${c.client_number}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <LifecyclePill status={c.lifecycle_status} />
                        <Icons.Plus className="h-4 w-4 text-slate-300 group-hover:text-blue-500" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchQ.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-3">No contacts found</p>
            )}
            {selectedContacts.length < 2 && (
              <p className="text-xs text-slate-400 text-center py-2" data-testid="merge-min-contacts-notice">
                Select at least one more contact to continue
              </p>
            )}
          </div>
        )}

        {/* ── Step 1: Survivor recommendation ────────────────────────────── */}
        {step === 1 && recommendation && (
          <div className="space-y-4" data-testid="merge-step-survivor">
            {/* Recommendation notice */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 flex items-start gap-2">
              <Icons.Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{recommendation.note}</span>
            </div>

            {/* Ranked contacts with scoring */}
            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">
                Choose the <strong>survivor</strong> (all other records will be retired):
              </div>
              <RadioGroup value={survivorId || ""} onValueChange={setSurvivorId}
                data-testid="merge-survivor-group">
                <div className="space-y-2">
                  {recommendation.contacts_ranked.map((ranked, i) => {
                    const contact = selectedContacts.find((c) => c.id === ranked.id) || ranked;
                    const isRecommended = i === 0;
                    const isSurvivor = survivorId === ranked.id;
                    return (
                      <div key={ranked.id} data-testid={`merge-survivor-option-${ranked.id}`}>
                        <ContactCard contact={contact}
                          label={isSurvivor ? "Survivor" : isRecommended ? "Recommended" : "Duplicate"}
                          selected={isSurvivor}
                          onSelect={setSurvivorId}
                          radioName="survivor"
                        />
                        <div className="mt-1 px-3">
                          <ScoreBar score={ranked.score} />
                          {ranked.reasons?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ranked.reasons.map((r) => (
                                <span key={r} className="text-[10px] bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">
                                  {r}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>

            {/* Override warning */}
            {survivorId && survivorId !== recommendation.recommended_survivor_id && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700"
                data-testid="merge-override-warning">
                <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  You have selected a different survivor than recommended. The system will proceed
                  with your choice, but please confirm this is the record with the most established
                  client relationship.
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Review & resolve ─────────────────────────────────────── */}
        {step === 2 && preflight && (
          <div className="space-y-4" data-testid="merge-step-review">
            {/* Blockers */}
            {hasBlockers && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-1.5" data-testid="merge-blockers">
                <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                  <Icons.AlertTriangle className="h-4 w-4" />
                  Cannot proceed — resolve these issues first:
                </div>
                {preflight.blockers.map((b, i) => (
                  <div key={i} className="text-sm text-red-700 pl-6">• {b}</div>
                ))}
              </div>
            )}

            {/* Warnings */}
            {preflight.warnings?.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="merge-warnings">
                <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-1">
                  <Icons.Info className="h-4 w-4" />Warnings
                </div>
                {preflight.warnings.map((w, i) => (
                  <div key={i} className="text-xs text-amber-700 pl-6">• {w}</div>
                ))}
              </div>
            )}

            {/* Survivor summary */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-1">
              <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider mb-2">
                Survivor (kept)
              </div>
              <div className="text-sm font-semibold text-slate-800">{preflight.survivor.full_name}</div>
              <div className="text-xs text-slate-500">{preflight.survivor.client_number}</div>
              {preflight.survivor.email && (
                <div className="text-xs text-slate-600">{preflight.survivor.email}</div>
              )}
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <LifecyclePill status={preflight.survivor.lifecycle_status} />
                {preflight.computed_lifecycle !== preflight.survivor.lifecycle_status && (
                  <span className="text-[10px] text-blue-600 bg-blue-100 rounded-full px-1.5 py-0.5">
                    Will update to {preflight.computed_lifecycle}
                  </span>
                )}
              </div>
            </div>

            {/* Duplicates summary */}
            <div data-testid="merge-duplicates-summary">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                To be retired ({preflight.duplicates.length}):
              </div>
              <div className="space-y-1.5">
                {preflight.duplicates.map((d) => (
                  <div key={d.id}
                    className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-500 line-through truncate">
                        {d.full_name}
                      </div>
                      <div className="text-xs text-slate-400">{d.client_number}</div>
                    </div>
                    <LifecyclePill status={d.lifecycle_status} />
                  </div>
                ))}
              </div>
            </div>

            {/* Portal disposition */}
            {preflight.portal_disposition?.action !== "none" && (
              <div className={`flex items-start gap-3 rounded-xl border p-3 text-sm
                ${preflight.portal_disposition.is_blocker
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-purple-200 bg-purple-50 text-purple-700"}`}
                data-testid="merge-portal-disposition">
                <Icons.Shield className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{preflight.portal_disposition.description}</span>
              </div>
            )}

            {/* Field conflicts */}
            {preflight.unresolved_conflicts?.length > 0 && (
              <div className="space-y-2" data-testid="merge-conflicts">
                <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Icons.AlertCircle className="h-4 w-4 text-amber-500" />
                  Profile conflicts — choose a value for each:
                </div>
                {preflight.unresolved_conflicts.map((c) => (
                  <ConflictRow key={c.field} conflict={c}
                    value={conflictResolutions[c.field]}
                    onChange={resolveConflict} />
                ))}
              </div>
            )}

            {/* Tax Profile conflicts */}
            {preflight.tp_conflicts?.length > 0 && (
              <div className="space-y-2" data-testid="merge-tp-conflicts">
                <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Icons.FileText className="h-4 w-4 text-purple-500" />
                  Tax Profile conflicts — review and choose:
                </div>
                <p className="text-xs text-slate-500">
                  Different tax years, filing statuses, and residency information may reflect
                  legitimate changes over time. Fields marked "Mandatory review" require your decision.
                </p>
                {preflight.tp_conflicts.map((c) => (
                  <TpConflictRow key={c.field} conflict={c}
                    value={tpResolutions[c.field]}
                    onChange={resolveTpConflict} />
                ))}
              </div>
            )}

            {/* Auto-fills */}
            {preflight.proposed_fills?.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="merge-auto-fills">
                <div className="text-xs font-semibold text-slate-600 mb-2">
                  Auto-filled from duplicate(s) ({preflight.proposed_fills.length} fields):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {preflight.proposed_fills.map((f) => (
                    <span key={f.field}
                      className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                      {f.field}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Reassignment counts */}
            {preflight.reassignment_total > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-3"
                data-testid="merge-reassignment-counts">
                <div className="text-xs font-semibold text-slate-600 mb-2">
                  Records to reassign ({preflight.reassignment_total} total):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(preflight.reassignment_counts)
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => (
                      <span key={k}
                        className="text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2 py-0.5">
                        {k}: {v}
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Finance counts */}
            {preflight.finance_total > 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3"
                data-testid="merge-finance-counts">
                <div className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                  <Icons.DollarSign className="h-3.5 w-3.5" />
                  Finance records to reassign ({preflight.finance_total} total):
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(preflight.finance_counts || {})
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => (
                      <span key={k}
                        className="text-xs bg-white text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        {k}: {v}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Confirm ──────────────────────────────────────────────── */}
        {step === 3 && preflight && (
          <div className="space-y-4" data-testid="merge-step-confirm">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
              <div className="text-sm font-semibold text-blue-800">Ready to merge</div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>{preflight.survivor.full_name}</strong> ({preflight.survivor.client_number}) becomes the canonical record</li>
                {preflight.duplicates.map((d) => (
                  <li key={d.id}>
                    • <strong>{d.full_name}</strong> ({d.client_number}) will be <strong>retired</strong>
                  </li>
                ))}
                <li>• {preflight.reassignment_total} related records will be reassigned</li>
                {(preflight.finance_total || 0) > 0 && (
                  <li>• {preflight.finance_total} finance records will be reconciled</li>
                )}
                {preflight.lifecycle_changed && (
                  <li>• Lifecycle updated to <strong>{preflight.computed_lifecycle}</strong></li>
                )}
                {preflight.portal_disposition?.action === "transfer_from_duplicate" && (
                  <li>• Portal access transferred to survivor</li>
                )}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5">
              <div className="text-xs font-semibold text-slate-600">After merging:</div>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• The surviving record will appear only once in CRM searches</li>
                <li>• All transferred information is accessible from the survivor's detail page</li>
                <li>• Retired records remain accessible through audit/history view with a link to the survivor</li>
                <li>• No contact is permanently deleted — only soft-retired</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400">
              This operation is not automatically reversible. Retired contacts will be tombstoned
              with a reference to the survivor.
            </p>
          </div>
        )}

        {/* ── Footer actions ───────────────────────────────────────────────── */}
        <DialogFooter className="gap-2 pt-2">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)}
              data-testid="merge-back-btn"
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              Back
            </button>
          )}

          {step === 0 && (
            <button onClick={proceedToRecommend}
              disabled={selectedContacts.length < 2 || loadingRec}
              data-testid="merge-next-btn"
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {loadingRec && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Continue
            </button>
          )}

          {step === 1 && (
            <button onClick={proceedToReview}
              disabled={!survivorId || preflightLoading}
              data-testid="merge-review-btn"
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {preflightLoading && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Review merge
            </button>
          )}

          {step === 2 && (
            <button onClick={() => setStep(3)}
              disabled={!canContinue || mandatoryTpUnresolved > 0}
              data-testid="merge-continue-btn"
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {!canContinue
                ? `Resolve ${unresolvedConflicts} conflict(s)`
                : mandatoryTpUnresolved > 0
                  ? `Resolve ${mandatoryTpUnresolved} tax profile field(s)`
                  : "Continue"}
            </button>
          )}

          {step === 3 && (
            <button onClick={executeMerge} disabled={merging}
              data-testid="merge-confirm-btn"
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {merging && <Icons.Loader2 className="h-4 w-4 animate-spin" />}
              Confirm merge
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
